import { Lock } from "lucide-react";

import { LockedBadge } from "@/components/locked/LockedBadge";
import { getLockedFeatureCopy } from "@/components/locked/featureCopy";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

type LockedComponentPlaceholderProps = {
  flag?: string;
  title?: string;
  description?: string;
  featureId?: string;
  className?: string;
};

export default function LockedComponentPlaceholder({
  flag,
  title,
  description,
  featureId,
  className,
}: LockedComponentPlaceholderProps) {
  const copy = flag ? getLockedFeatureCopy(flag) : undefined;
  const resolvedTitle = title ?? copy?.title ?? "Upgrade to unlock";
  const resolvedDescription =
    description ??
    copy?.description ??
    "This capability lives in the Pro toolkit. Unlock automation, routing, and CRM tools by upgrading.";
  const resolvedFeature = featureId ?? copy?.featureName ?? flag ?? resolvedTitle;

  const tile = (
    <div
      className={cn(
        "w-full cursor-pointer rounded-2xl border border-border bg-background/80 px-4 py-3 text-sm text-foreground shadow-sm transition hover:border-amber-300",
        className,
      )}
      aria-label={`Locked component: ${resolvedTitle}`}
    >
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-full border border-amber-400/40 bg-amber-400/10">
          <Lock className="h-4 w-4 text-primary-foreground dark:text-amber-200" />
        </div>
        <div className="flex-1 space-y-1">
          <p className="text-[10px] uppercase tracking-[0.4em] text-amber-300">Locked component</p>
          <p className="text-sm font-semibold leading-tight text-foreground">{resolvedTitle}</p>
          <p className="text-xs leading-snug text-foreground/70 line-clamp-2">{resolvedDescription}</p>
        </div>
        <div className="flex flex-col items-end justify-between">
          <LockedBadge feature={resolvedFeature} className="self-start" />
        </div>
      </div>
    </div>
  );

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{tile}</TooltipTrigger>
        <TooltipContent className="max-w-xs space-y-1 text-sm leading-tight">
          <p className="font-semibold text-foreground">Upgrade to unlock</p>
          <p className="text-xs text-foreground/70">{resolvedTitle}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
